import { create } from 'zustand';
import achievementService from '../api/achievementService';
import logger from '../utils/logger';

// ponytail: Zustand store replacing the AchievementContext boilerplate
export const useAchievementStore = create((set, get) => ({
  achievements: [],
  loading: true,
  error: null,

  fetchAchievements: async (params = {}) => {
    try {
      set({ loading: true });
      const res = await achievementService.getAchievements(params);
      const list = Array.isArray(res.data?.achievements) ? res.data.achievements : [];
      set({ achievements: list, error: null });
    } catch (err) {
      logger.error('Error fetching achievements:', err);
      set({ error: err.response?.data?.message || 'Failed to load achievements' });
    } finally {
      set({ loading: false });
    }
  },

  addAchievement: async (payload) => {
    try {
      const res = await achievementService.createAchievement(payload);
      set((state) => ({ achievements: [...state.achievements, res.data.achievement] }));
      return { success: true, data: res.data.achievement };
    } catch (err) {
      logger.error('Error adding achievement:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to create achievement' };
    }
  },

  updateAchievement: async (id, payload) => {
    try {
      const res = await achievementService.updateAchievement(id, payload);
      set((state) => ({
        achievements: state.achievements.map((a) => (a._id === id ? res.data.achievement : a)),
      }));
      return { success: true, data: res.data.achievement };
    } catch (err) {
      logger.error('Error updating achievement:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to update achievement' };
    }
  },

  deleteAchievement: async (id) => {
    try {
      await achievementService.deleteAchievement(id);
      set((state) => ({ achievements: state.achievements.filter((a) => a._id !== id) }));
      return { success: true };
    } catch (err) {
      logger.error('Error deleting achievement:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete achievement' };
    }
  },

  bulkDelete: async (ids) => {
    try {
      await achievementService.bulkDeleteAchievements(ids);
      set((state) => ({ achievements: state.achievements.filter((a) => !ids.includes(a._id)) }));
      return { success: true };
    } catch (err) {
      logger.error('Error bulk deleting achievements:', err);
      return { success: false, message: err.response?.data?.message || 'Failed to delete achievements' };
    }
  },

  byCategory: (category) => {
    const list = Array.isArray(get().achievements) ? get().achievements : [];
    return list.filter((a) => a.category === category && a.isActive);
  },

  stats: () => {
    const list = Array.isArray(get().achievements) ? get().achievements : [];
    return list.filter((a) => a.category === 'stat' && a.isActive).sort((a, b) => (a.order || 0) - (b.order || 0));
  },
}));
